<template>
    <div v-if="isNavigationPending" class="pt-4 pb-2" aria-hidden="true">
        <div class="flex gap-2 flex-nowrap overflow-hidden">
            <div
                v-for="(width, index) in skeletonWidths"
                :key="index"
                class="shrink-0"
                :style="{ width }"
            >
                <MiscSkeleton height="36px" color="bg-base-250" />
            </div>
        </div>
    </div>
    <div v-else-if="subcategories.length > 0" class="pt-4 pb-2">
        <div ref="subcategoryContainer" class="flex gap-2 flex-nowrap overflow-x-scroll no-scrollbar snap-x snap-proximity">
            <MiscLink
                v-for="subcategory in subcategories"
                :key="subcategory.id"
                :to="getCategoryRoute(subcategory)"
                :aria-current="isActiveSubcategory(subcategory.id) ? 'page' : undefined"
                class="px-4 py-2 text-xs font-semibold border border-base-250 bg-base-250 whitespace-nowrap rounded-xs"
                :class="{ 'border border-base-content bg-transparent': isActiveSubcategory(subcategory.id) }"
                no-prefetch
            >
                {{ getTranslatedProperty(subcategory, 'name') }}
            </MiscLink>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getTranslatedProperty, getCategoryRoute } from '@shopware/helpers'

// Horizontal subcategory navigation for the current category. Visibility per viewport is up to the caller.
const navigation = inject<Ref<Schemas['NavigationRouteResponse'] | null>>('mainNavigation', ref(null))
const isNavigationPending = inject<Ref<boolean>>('mainNavigationPending', ref(false))

// Needs the deep category tree, which is deferred (see layouts/default.vue).
// Trigger it on mount so it populates without waiting for a mega-menu interaction.
// Idempotent — coalesces with any other trigger on the page.
const loadMainNavigation = inject<() => void>('loadMainNavigation', () => {})
onMounted(loadMainNavigation)

// Varying widths so the placeholder resembles real navigation items
const skeletonWidths = ['6rem', '8rem', '5rem', '7rem', '6rem']

const { category } = useCategory()

const subcategoryContainer = ref<HTMLElement | null>(null)

// Type for navigation category items with children
type NavigationCategory = Schemas['Category']

// Get subcategories from navigation or siblings if no children
const subcategories = computed(() => {
    const currentCategoryId = category.value?.id

    if (!currentCategoryId || !navigation.value) {
        return []
    }

    // Find the current category and its parent in navigation tree
    const findCategoryWithParent = (
        navItems: NavigationCategory[],
        parent: NavigationCategory | null = null
    ): { category: NavigationCategory, parent: NavigationCategory | null } | null => {
        for (const item of navItems) {
            if (item.id === currentCategoryId) {
                return { category: item, parent }
            }
            if (item.children && item.children.length > 0) {
                const found = findCategoryWithParent(item.children, item)
                if (found) return found
            }
        }
        return null
    }

    const result = findCategoryWithParent(navigation.value)

    if (!result) return []

    // If current category has children, show them
    if (result.category.children && result.category.children.length > 0) {
        return result.category.children.filter(cat =>
            !cat.customFields?.custom_category_flyout_section
        )
    }

    // If no children, show siblings (categories at the same level)
    if (result.parent && result.parent.children) {
        return result.parent.children.filter(cat =>
            !cat.customFields?.custom_category_flyout_section
        )
    }

    return []
})

// Check if a subcategory is the currently active one
const isActiveSubcategory = (subcategoryId: string) => {
    return subcategoryId === category.value?.id
}

// Align the active subcategory to the start of the row (like the tab slider's inline: 'start').
// Scrolls only the container: scrollIntoView() would also scroll the page vertically when the
// row is out of view, e.g. during scroll restoration after a reload.
const scrollActiveIntoView = () => {
    const container = subcategoryContainer.value
    const activeElement = container?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!container || !activeElement) return

    container.scrollTo({
        left: activeElement.getBoundingClientRect().left - container.getBoundingClientRect().left + container.scrollLeft,
        behavior: 'smooth'
    })
}

// flush: 'post' runs after the DOM has rendered the (newly loaded) subcategories
watch(subcategories, scrollActiveIntoView, { flush: 'post' })
onMounted(scrollActiveIntoView)
</script>
